
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
const WS_URL = API_BASE_URL
    .replace('http', 'ws')
    .replace('https', 'ws')+"/ws/export";

export interface ExportProgress {
    total: number;
    current: number;
    scrollId: string;
    entityType: string;
    hasMore: boolean;
}

class ExportService {
    private socket: WebSocket | null = null;
    private writable: any = null; // FileSystemWritableFileStream
    private headersWritten = false;
    private isCancelled = false;

    private jsonToCsvRow(obj: any, headers: string[]): string {
        return headers.map(header => {
            let value = obj[header];
            if (value === null || value === undefined) value = '';
            if (typeof value === 'object') value = JSON.stringify(value);
            const stringValue = String(value);
            return `"${stringValue.replace(/"/g, '""')}"`;
        }).join(',') + '\n';
    }

    cancelExport() {
        this.isCancelled = true;
        if (this.socket) {
            this.socket.close();
            this.socket = null;
        }
        console.log('Export cancelled by user');
    }

    async startExport(
        entityType: string,
        companyId: string,
        entityId: string,
        onProgress: (progress: ExportProgress) => void
    ): Promise<void> {
        const token = localStorage.getItem('token');
        this.isCancelled = false;
        this.headersWritten = false;
        this.writable = null;

        console.log(`Starting export for ${entityType} (Company: ${companyId})`);

        // Try to use File System Access API for direct saving
        try {
            if ('showSaveFilePicker' in window) {
                const handle = await (window as any).showSaveFilePicker({
                    suggestedName: `${entityType.toLowerCase()}_export_${new Date().toISOString().split('T')[0]}.csv`,
                    types: [{
                        description: 'CSV File',
                        accept: { 'text/csv': ['.csv'] },
                    }],
                });
                this.writable = await handle.createWritable();
                console.log('File System Access API: Writable handle created');
            }
        } catch (err) {
            console.warn('File System Access API not available or cancelled, will use memory buffer', err);
        }

        return new Promise((resolve, reject) => {
            try {
                // Use WebSocket for streaming data
                const wsUrlWithToken = `${WS_URL}?token=${token}`;
                this.socket = new WebSocket(wsUrlWithToken);

                let currentCount = 0;
                let allData: any[] = [];

                this.socket.onopen = () => {
                    console.log('WebSocket connection established');
                    // Check for existing scrollId in localStorage for resumption
                    const savedExport = localStorage.getItem(`export_${entityType}_${companyId}`);
                    if (savedExport) {
                        try {
                            const { scrollId, current, entityType: savedType } = JSON.parse(savedExport);
                            if (savedType === entityType) {
                                console.log(`Resuming export with scrollId: ${scrollId} from count: ${current}`);
                                currentCount = current;
                                this.socket?.send(JSON.stringify({
                                    action: 'NEXT',
                                    scrollId
                                }));
                                return;
                            }
                        } catch (e) {
                            console.error('Failed to parse saved export state', e);
                            localStorage.removeItem(`export_${entityType}_${companyId}`);
                        }
                    }

                    console.log('Sending START action');
                    this.socket?.send(JSON.stringify({
                        action: 'START',
                        entityType,
                        companyId,
                        entityId,
                        pageSize: 100
                    }));
                };

                this.socket.onmessage = async (event) => {
                    if (this.isCancelled) return;

                    try {
                        const response = JSON.parse(event.data);
                        if (response.error) {
                            console.error('Export error from server:', response.error);
                            reject(new Error(response.error));
                            this.socket?.close();
                            return;
                        }

                        const { data, scrollId, hasMore, totalElements } = response;

                        if (data && data.length > 0) {
                            const headers = Object.keys(data[0]);

                            if (this.writable) {
                                if (!this.headersWritten) {
                                    await this.writable.write(headers.join(',') + '\n');
                                    this.headersWritten = true;
                                }
                                for (const item of data) {
                                    await this.writable.write(this.jsonToCsvRow(item, headers));
                                }
                            } else {
                                allData = [...allData, ...data];
                            }

                            currentCount += data.length;
                            console.log(`Received ${data.length} items. Progress: ${currentCount}/${totalElements}`);

                            // Save state for resumption
                            if (hasMore) {
                                localStorage.setItem(`export_${entityType}_${companyId}`, JSON.stringify({
                                    scrollId,
                                    current: currentCount,
                                    entityType
                                }));
                            } else {
                                localStorage.removeItem(`export_${entityType}_${companyId}`);
                            }

                            onProgress({
                                total: totalElements,
                                current: currentCount,
                                scrollId,
                                entityType,
                                hasMore
                            });

                            if (hasMore) {
                                this.socket?.send(JSON.stringify({
                                    action: 'NEXT',
                                    scrollId
                                }));
                            } else {
                                console.log('Export completed successfully');
                                if (this.writable) {
                                    await this.writable.close();
                                } else {
                                    this.downloadAsBlob(allData, entityType);
                                }
                                this.socket?.close();
                                resolve();
                            }
                        } else {
                            console.log('No data received or export finished');
                            if (this.writable) {
                                await this.writable.close();
                            } else if (allData.length > 0) {
                                this.downloadAsBlob(allData, entityType);
                            }
                            this.socket?.close();
                            resolve();
                        }
                    } catch (e) {
                        console.error('Error processing WebSocket message:', e);
                        reject(e);
                        this.socket?.close();
                    }
                };

                this.socket.onerror = (error) => {
                    console.error('WebSocket error:', error);
                    reject(new Error('WebSocket connection failed'));
                };

                this.socket.onclose = (event) => {
                    console.log(`WebSocket closed (Code: ${event.code}, Reason: ${event.reason})`);
                    if (!event.wasClean && !this.isCancelled) {
                        reject(new Error(`Connection closed unexpectedly: ${event.reason || 'Unknown reason'}`));
                    }
                };
            } catch (e) {
                console.error('Failed to initialize export:', e);
                reject(e);
            }
        });
    }

    private downloadAsBlob(data: any[], entityType: string) {
        if (data.length === 0) {
            console.warn('No data to download as Blob');
            return;
        }
        console.log(`Downloading ${data.length} items as Blob`);
        const headers = Object.keys(data[0]);
        const csvContent = headers.join(',') + '\n' +
            data.map(row => this.jsonToCsvRow(row, headers)).join('');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `${entityType.toLowerCase()}_export_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }
}

export const exportService = new ExportService();
